import { ApolloError } from '@apollo/client';
import { MockList } from '@graphql-tools/mock';
import { v4 as createUuid } from 'uuid';

import { mocks } from 'src/modules/api-client/mocks/spec/';

import availabilityOptionsData from '../spec/AvailabilityOptions';
import preferredClassificationOptionsData from '../spec/PreferredClassificationOptions';
import rightToWorkOptionsData from '../spec/RightToWorkOptions';
import salaryOptions from '../spec/SalaryOptions';
import supportedCountries from '../spec/SupportedCountries';
import workTypeOptionsData from '../spec/WorkTypeOptions';

import {
  salaryPreferences,
  emptyRightToWork,
  rightsToWork as rightsToWorkData,
  rightToWorkVerified,
  personalDetails as personalDetailsData,
  emptyPersonalDetails,
  currentLocation2 as currentLocation2Data,
  licences as licenceData,
  personalStatement,
  profileVisibility as profileVisibilityData,
  profileVisibility2 as profileVisibility2Data,
  workTypes as workTypesData,
  availability as availabilityData,
  preferredClassification as preferredClassificationData,
  preferredLocations2 as preferredLocations2Data,
  skills as skillsData,
  oneOfEachResumeStatus,
  noDefaultResume,
  tenResumes,
  languageProficiencies as languageProficienciesData,
  personalDetailsNoCountryCallingCode,
  personalDetailsNoPhoneNumber,
  approachability as approachabilityData,
  _id,
  parsedRole,
  parsedQualification,
  scoreNoTips,
  scoreMultipleTips,
  scoreSingleTip,
  deprecatedHomeLocation,
  preferredDeprecatedLocations2,
  deprecatedRightsToWork,
  profileInsightsWithActivities,
  profileInsightsWithoutSearchImpressions,
  profileSuggestionsWithData,
  profileSuggestionsWithoutData,
  profileSuggestionsWithSingleData,
  allRightsToWork,
  withSPIResume,
  verifiedLicencesWithExpiryStatus,
  referenceChecksWithData,
  publicProfileData,
  profileVisibilityOptionsWithData,
  createPlaceholderAvatarDataUri as createPlaceholderAvatarDataUriBase,
} from './test-data';
import { getStateTesters, handleQueryError } from './utils';

const { returnState, returnEmptyState } = getStateTesters();

export const MutationResult = {
  ref: '1234',
};

const approachability = () => {
  handleQueryError('approachability');

  return approachabilityData;
};

const availabilityOptions = () => {
  handleQueryError('availabilityOptions');

  if (returnEmptyState('availabilityOptions')) {
    return [];
  }

  return availabilityOptionsData();
};

const workTypeOptions = () => {
  handleQueryError('workTypeOptions');

  if (returnEmptyState('workTypeOptions')) {
    return [];
  }

  return workTypeOptionsData();
};

const salaryPreferences2 = () => {
  handleQueryError('salaryPreferences');

  if (returnEmptyState('salaryPreferences')) {
    return [salaryPreferences[0]];
  }

  return salaryPreferences;
};

const rightsToWork = () => {
  handleQueryError('rightsToWork');

  if (returnEmptyState('rightsToWork')) {
    return [emptyRightToWork];
  }

  const verifiedState = returnState('rightsToWork', 'verified');
  if (verifiedState) {
    return rightToWorkVerified;
  }

  if (returnState('rightsToWork', 'deprecated')) {
    return deprecatedRightsToWork;
  }
  if (returnState('rightsToWork', 'all')) {
    return allRightsToWork;
  }

  return rightsToWorkData;
};

const rightToWorkOptions = () => {
  handleQueryError('rightToWorkOptions');

  if (returnEmptyState('rightToWorkOptions')) {
    return [];
  }

  return rightToWorkOptionsData();
};

const licences = () => {
  handleQueryError('licences');

  if (returnEmptyState('licences')) {
    return [];
  }

  if (returnState('licences', 'withExpiryStatus')) {
    return verifiedLicencesWithExpiryStatus;
  }

  return licenceData;
};

const referenceChecks = () => {
  handleQueryError('referenceChecks');

  if (returnEmptyState('referenceChecks')) {
    return [];
  }

  return referenceChecksWithData;
};

const personalDetails = () => {
  handleQueryError('personalDetails');

  if (returnEmptyState('personalDetails')) {
    return emptyPersonalDetails;
  }

  if (returnState('personalDetails', 'noCountryCallingCode')) {
    return personalDetailsNoCountryCallingCode;
  }

  if (returnState('personalDetails', 'noPhoneNumber')) {
    return personalDetailsNoPhoneNumber;
  }

  return personalDetailsData;
};

const currentLocation2 = () => {
  handleQueryError('currentLocation2');

  if (returnEmptyState('currentLocation2')) {
    return null;
  }

  if (returnState('homeLocation', 'deprecated')) {
    return deprecatedHomeLocation;
  }

  return currentLocation2Data;
};

const careerObjectives = () => {
  handleQueryError('careerObjectives');

  if (returnEmptyState('careerObjectives')) {
    return null;
  }

  return personalStatement;
};

const profileVisibility = () => {
  handleQueryError('profileVisibility');

  if (returnEmptyState('profileVisibility')) {
    return null;
  }

  if (returnState('profileVisibility', 'Standard')) {
    return { level: 'Standard' };
  }

  if (returnState('profileVisibility', 'Limited')) {
    return { level: 'Limited' };
  }

  if (returnState('profileVisibility', 'Hidden')) {
    return { level: 'Hidden' };
  }

  return profileVisibilityData;
};

const publicProfile = () => {
  handleQueryError('publicProfile');

  if (returnEmptyState('publicProfile')) {
    return null;
  }

  return publicProfileData;
};

const profileVisibility2 = () => {
  handleQueryError('profileVisibility2');

  if (returnEmptyState('profileVisibility2')) {
    return null;
  }

  if (returnState('profileVisibility2', 'Public')) {
    return { id: 'Public', __typename: 'PublicVisibilityOption' };
  }
  if (returnState('profileVisibility2', 'Standard')) {
    return { id: 'Standard', __typename: 'StandardVisibilityOption' };
  }

  if (returnState('profileVisibility2', 'Limited')) {
    return { id: 'Limited', __typename: 'LimitedVisibilityOption' };
  }

  if (returnState('profileVisibility2', 'Hidden')) {
    return { id: 'Hidden', __typename: 'HiddenVisibilityOption' };
  }

  return profileVisibility2Data;
};

const workTypes = () => {
  handleQueryError('workTypes');

  if (returnEmptyState('workTypes')) {
    return [];
  }

  return workTypesData;
};

const availability = () => {
  handleQueryError('availability');

  if (returnEmptyState('availability')) {
    return null;
  }

  return availabilityData;
};

const preferredClassification = () => {
  handleQueryError('preferredClassification');

  if (returnEmptyState('preferredClassification')) {
    return null;
  }

  return preferredClassificationData;
};

const preferredClassificationOptions = () => {
  handleQueryError('preferredClassificationOptions');

  if (returnEmptyState('preferredClassificationOptions')) {
    return null;
  }

  return preferredClassificationOptionsData();
};

const preferredLocations2 = () => {
  handleQueryError('preferredLocations');

  if (returnEmptyState('preferredLocations')) {
    return [];
  }
  if (returnState('preferredLocation', 'deprecated')) {
    return preferredDeprecatedLocations2;
  }

  return preferredLocations2Data;
};

const skills = () => {
  handleQueryError('skills');

  if (returnEmptyState('skills')) {
    return [];
  }

  return skillsData;
};

const resumes = (options: { skipQueryError?: boolean } = {}) => {
  if (!options?.skipQueryError) {
    handleQueryError('resumes');
  }

  if (returnEmptyState('resumes')) {
    return [];
  }

  if (returnState('resumes', 'resumeLimit')) {
    return tenResumes;
  }

  if (returnState('resumes', 'noDefaultResume')) {
    return noDefaultResume;
  }
  if (returnState('resumes', 'withSPIResume')) {
    return withSPIResume;
  }

  return oneOfEachResumeStatus;
};

const resume = () => oneOfEachResumeStatus[4];

const languageProficiencies = () => {
  handleQueryError('languages');

  if (returnEmptyState('languages')) {
    return [];
  }

  return languageProficienciesData;
};

const score = () => {
  handleQueryError('score');

  if (returnEmptyState('score')) {
    return scoreNoTips;
  }

  if (returnState('score', 'single')) {
    return scoreSingleTip;
  }

  if (returnState('score', 'multiple')) {
    return scoreMultipleTips;
  }
};

const profileSuggestions = () => {
  handleQueryError('suggestions');

  if (returnEmptyState('suggestions')) {
    return profileSuggestionsWithoutData;
  }

  if (returnState('suggestions', 'single')) {
    return profileSuggestionsWithSingleData;
  }

  if (returnState('suggestions', 'multiple')) {
    return profileSuggestionsWithData;
  }
};

const unconfirmedDataForContext = () => {
  if (returnState('unconfirmedData', 'found')) {
    return {
      __typename: 'UnconfirmedDataCompleted',
      completed: true,
      roles: [parsedRole],
      qualifications: [parsedQualification],
    };
  }
  return {
    __typename: 'UnconfirmedDataPending',
    completed: false,
  };
};

const profileInsights2 = () => {
  handleQueryError('profileInsights2');

  if (returnState('profileInsights', 'noSearchImpressions')) {
    return profileInsightsWithoutSearchImpressions;
  }

  if (returnEmptyState('profileInsights')) {
    return null;
  }

  return profileInsightsWithActivities;
};

const Candidate = () => {
  if (returnState('page', 'auth-error')) {
    const authError = new Error('Auth request failed');
    // @ts-expect-error
    authError.extensions = { code: 'UNAUTHENTICATED' };
    throw authError;
  }

  return {
    // eslint-disable-next-line new-cap
    ...mocks.Candidate(),
    _id,
    approachability,
    emailAddress: 'henry_perez@gmail.net',
    salaryPreferences2,
    rightsToWork,
    licences,
    referenceChecks,
    personalDetails,
    currentLocation2,
    careerObjectives,
    profileVisibility,
    publicProfile,
    profileVisibility2,
    workTypes,
    availability,
    preferredClassification,
    preferredLocations2,
    skills,
    resumes,
    resume,
    languageProficiencies,
    score,
    profileSuggestions,
    unconfirmedDataForContext,
    profileInsights2,
  };
};

const profileVisibilityOptions = () => {
  handleQueryError('profileVisibilityOptions');

  if (returnEmptyState('profileVisibilityOptions')) {
    return [];
  }

  return profileVisibilityOptionsWithData;
};

const createGraphQLError = (message: string, code: string) =>
  new ApolloError({
    graphQLErrors: [
      {
        message,
        path: ['viewer'],
        extensions: { code },
      },
    ],
    networkError: null,
  });

/**
 * Generates a data URI for a placeholder avatar image.
 * Uses an inline SVG to avoid external network dependencies.
 * This ensures mocks work offline and don't rely on third-party services.
 *
 * Supports error states via query parameters:
 * - `?testData=profileAvatar:error` - Returns invalid URL to trigger image load error
 * - `?testData=profileAvatar:empty` - Returns undefined (no avatar)
 */
const createPlaceholderAvatarDataUri = (): string | undefined => {
  // Check for error states
  if (returnState('profileAvatar', 'error')) {
    // Return invalid URL that will trigger onError handler in PlaceholderAvatar component
    return 'data:image/invalid';
  }
  if (returnEmptyState('profileAvatar')) {
    // Return undefined to simulate no avatar
    return undefined;
  }

  // Default: return valid placeholder image using shared function
  return createPlaceholderAvatarDataUriBase();
};

export const mockOverrides = {
  ...mocks,
  MutationResult,
  Candidate,
  resumes,
  ProfileAvatar: () => {
    const image128Url = createPlaceholderAvatarDataUri();
    // Return null when empty (no avatar), otherwise return object with id and image128Url
    return image128Url ? { id: createUuid(), image128Url } : null;
  },
  CreateProfileAvatarError: () => ({
    message: () => 'Failed to create avatar upload URL. Please try again.',
  }),
  PublicProfile: () => ({
    url: () => publicProfileData.url,
    displayUrl: () => publicProfileData.displayUrl,
  }),
  SeekDateTime: () => ({
    shortLabel: () => '7 Aug 2025',
  }),
  Query: () => ({
    viewer: () => {
      if (returnState('page', 'load-error')) {
        throw createGraphQLError(
          'Failed to load profile',
          'INTERNAL_SERVER_ERROR',
        );
      }
    },
    ontologyEducationProviders: () => new MockList([10, 10]),
    ontologyOrganisations: () => new MockList([10, 10]),
    ontologyRoleTitlesSeniority: () => new MockList([10, 10]),
    ontologyQualifications: () => new MockList([10, 10]),
    salaryOptions,
    rightToWorkOptions,
    availabilityOptions,
    workTypeOptions,
    preferredClassificationOptions,
    supportedCountries,
    profileVisibilityOptions,
  }),
};
